// SmsOptIn — reusable, self-contained SMS opt-in block for the second step of any
// intake form (registration, program self-register, lead magnet, call RSVP) and the
// standalone /sms page.
//
// Design contract:
//   - Registration stays EMAIL-FIRST. This block only appears once we already have
//     the person's email (step 2) — it NEVER gates the primary conversion.
//   - Transactional (reminders) and marketing (promotions) are SEPARATE consents —
//     two independent, UNCHECKED, OPTIONAL checkboxes. A visitor can take reminders
//     and decline promos, or neither.
//   - Each consent checkbox carries the COMPLETE carrier opt-in language inline
//     (brand, use case, frequency, rates, STOP/HELP, "not a condition of purchase").
//     A 10DLC reviewer requires the full disclosure ON the checkbox. Sentences are
//     fetched from GET /api/public/sms/disclosures so what's shown == what the
//     server stores as consent evidence.
//   - CARRIER RULE (Telnyx 10DLC — 4th iteration, 2026-07-06): neither the phone
//     field nor a consent checkbox may be required to COMPLETE the form, and
//     "optional" must be OBSERVABLE to a reviewer clicking through, not just true
//     in code. A form that refuses to finish until a box is ticked (even with a
//     gentle note) reads as a mandatory checkbox — that exact design was rejected.
//     So on the standalone /sms page: EMAIL is the required anchor field (email is
//     outside the carrier rule), the mobile field is labelled "(optional)", both
//     checkboxes are labelled "Optional —", and every combination completes
//     successfully — including zero boxes ticked (saves the contact, records NO
//     SMS consent, stores no number, and says so). A mobile number is only
//     required once a text type is actually chosen, which every compliant form
//     inherently needs.
//   - It posts INDEPENDENTLY to /api/public/sms/optin, so it drops into any form
//     without threading phone/consent through that form's own backend.

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { CountrySelect } from './CountrySelect';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

// Baked-in fallback so the disclosures ALWAYS render even if the fetch fails — an
// opt-in with no visible disclosures is not a compliant opt-in. Kept in sync with
// worker/src/sms-disclosures.ts (a QA case asserts parity).
const FALLBACK = {
  brandName: 'The Successful Bookkeeper',
  consent: {
    transactional:
      'Text me reminders for my sessions, calls, and events. By providing your mobile number and ' +
      'checking this box, you agree to receive recurring automated reminder text messages from The Successful Bookkeeper. ' +
      'Message frequency varies. Message & data rates may apply. Reply STOP to cancel at any time. Reply HELP for help. ' +
      'Consent is not a condition of any purchase.',
    marketing:
      'Text me occasional offers and announcements. By providing your mobile number and ' +
      'checking this box, you agree to receive recurring automated marketing & promotional text messages from The Successful Bookkeeper. ' +
      'Message frequency varies. Message & data rates may apply. Reply STOP to cancel at any time. Reply HELP for help. ' +
      'Consent is not a condition of any purchase.',
  },
  termsUrl: 'https://www.thesuccessfulbookkeeper.com/terms-of-use',
  privacyUrl: 'https://www.thesuccessfulbookkeeper.com/privacy-policy',
};

interface Disclosures {
  brandName: string;
  consent: { transactional: string; marketing: string };
  termsUrl: string;
  privacyUrl: string;
}

export type SmsOptInSource = 'sms-optin-page' | 'registration' | 'program-register' | 'magnet' | 'call-rsvp' | 'waitlist' | 'webinar-reg' | 'booking';

interface Props {
  /** Email already collected in step 1 — links the consent to the contact. */
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  source: SmsOptInSource;
  sourceDetail?: string | null;
  /** Heading copy override. */
  heading?: string;
  /** Style overrides for the outer card so it blends into each host form. */
  style?: CSSProperties;
  /**
   * Standalone mode for the dedicated /sms page — the asset a 10DLC carrier
   * reviewer fetches and clicks through. Renders on first paint: a required email
   * field (the anchor), the mobile-number field labelled optional, BOTH unchecked
   * consent checkboxes labelled optional with full language, and the submit
   * button. The form completes successfully in every combination — nothing but
   * email ever blocks it. Embedded uses (registration step 2, magnets, call RSVP)
   * keep the progressive-disclosure flow (phone revealed once a box is ticked).
   */
  standalone?: boolean;
  /**
   * When true, renders as a compact button ("Want a text reminder before we go
   * live?") that expands into the full card in place on click. The full
   * disclosure text is never hidden once expanded — this only defers the
   * initial paint, it doesn't shrink the carrier-mandated language itself.
   * Default false — every other embed (BKOS, magnets, calls, waitlist, /sms)
   * keeps rendering the full card immediately.
   */
  collapsible?: boolean;
  /** Button copy shown before expansion. Only used when collapsible is true. */
  collapsedLabel?: string;
}

export function SmsOptIn({ email, firstName, lastName, source, sourceDetail, heading, style, standalone, collapsible, collapsedLabel }: Props) {
  const [disc, setDisc] = useState<Disclosures>(FALLBACK);
  const [emailInput, setEmailInput] = useState(email ?? '');
  const [reminders, setReminders] = useState(false);
  const [promotions, setPromotions] = useState(false);
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!collapsible);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/public/sms/disclosures`)
      .then((r) => r.json())
      .then((d: Partial<Disclosures>) => {
        if (cancelled || !d?.consent?.transactional || !d?.consent?.marketing) return;
        setDisc({
          brandName: d.brandName ?? FALLBACK.brandName,
          consent: { transactional: d.consent.transactional, marketing: d.consent.marketing },
          termsUrl: d.termsUrl ?? FALLBACK.termsUrl,
          privacyUrl: d.privacyUrl ?? FALLBACK.privacyUrl,
        });
      })
      .catch(() => { /* fallback already rendered */ });
    return () => { cancelled = true; };
  }, []);

  const anyChecked = reminders || promotions;
  const effectiveEmail = standalone ? emailInput.trim() : (email ?? null);

  const submit = async () => {
    setError(null);
    setNote(null);
    if (standalone && !emailInput.trim()) { setError('Enter your email address.'); return; }
    // A mobile number is only needed once a text type is actually chosen.
    if (anyChecked && !phone.trim()) { setError('Enter your mobile number so we can text you.'); return; }

    if (!anyChecked) {
      // Embedded step-2 can't reach here (the button only renders once a box is
      // ticked) — kept as a safety net so the block stays honest if reused.
      if (!standalone) {
        setNote("You haven't chosen a text type yet, so nothing was saved. Tick a box above whenever you'd like to subscribe.");
        return;
      }
      // Standalone with zero boxes ticked: the checkboxes are genuinely optional,
      // so the form still COMPLETES — the contact is saved by email, NO SMS
      // consent is recorded, and no phone number is stored (there is no consent
      // context for one). Nothing will ever be sent.
      setStatus('saving');
      try {
        const res = await fetch(`${API_BASE}/api/public/sms/optin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone: null,
            country: country || null,
            transactional: false,
            marketing: false,
            email: effectiveEmail,
            first_name: firstName ?? null,
            last_name: lastName ?? null,
            source,
            source_detail: sourceDetail ?? null,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.success) {
          setStatus('error');
          setError(data?.message ?? 'Could not save your details. Please try again.');
          return;
        }
        setStatus('saved');
      } catch {
        setStatus('error');
        setError('Network error. Please try again.');
      }
      return;
    }

    setStatus('saving');
    try {
      const res = await fetch(`${API_BASE}/api/public/sms/optin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phone.trim(),
          country: country || null,
          transactional: reminders,
          marketing: promotions,
          email: effectiveEmail,
          first_name: firstName ?? null,
          last_name: lastName ?? null,
          source,
          source_detail: sourceDetail ?? null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        setStatus('error');
        setError(data?.message ?? 'Could not save your number. Please check it and try again.');
        return;
      }
      setStatus('done');
    } catch {
      setStatus('error');
      setError('Network error. Please try again.');
    }
  };

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        style={{ ...collapsedBtnStyle, ...style }}
        data-sms-optin="collapsed"
      >
        {collapsedLabel ?? 'Want a text reminder before we go live?'}
      </button>
    );
  }

  if (status === 'done') {
    return (
      <div style={{ ...cardBase, ...style }} data-sms-optin="done">
        <div style={{ fontWeight: 700, color: '#0A0A2E', marginBottom: 4 }}>You're set for texts ✓</div>
        <div style={{ fontSize: 13, color: '#555' }}>
          We'll only text you what you asked for. Reply STOP any time to cancel.
        </div>
      </div>
    );
  }

  if (status === 'saved') {
    return (
      <div style={{ ...cardBase, ...style }} data-sms-optin="saved">
        <div style={{ fontWeight: 700, color: '#0A0A2E', marginBottom: 4 }}>You're on our list ✓</div>
        <div style={{ fontSize: 13, color: '#555' }}>
          You didn't subscribe to any texts, so we won't send you any. Come back and tick a
          box whenever you'd like reminders or offers.
        </div>
      </div>
    );
  }

  // Phone + country block. In standalone (/sms) mode it's always visible so the
  // carrier-reviewed page shows a mobile-number field with zero interaction; in
  // embedded mode it appears once a consent box is ticked.
  const phoneBlock = (
    <div style={{ display: 'flex', gap: 8, margin: '6px 0 10px', flexWrap: 'wrap' }}>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="Mobile number"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        style={inputStyle}
        aria-label="Mobile number"
      />
      <CountrySelect value={country} onChange={setCountry} style={{ ...inputStyle, maxWidth: 160 }} />
    </div>
  );

  return (
    <div style={{ ...cardBase, ...style }} data-sms-optin="form">
      <div style={{ fontWeight: 700, color: '#0A0A2E', marginBottom: 8 }}>
        {heading ?? 'Want a text reminder?'}
      </div>

      {/* Standalone: email is the required anchor field; the mobile number is
          explicitly optional. Field-level "(required)"/"(optional)" labels exist so
          a carrier reviewer can SEE which is which without testing. */}
      {standalone && (
        <>
          <label htmlFor="sms-optin-email" style={fieldLabel}>
            Email <span style={reqTag}>(required — to link your number)</span>
          </label>
          <input
            id="sms-optin-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', flex: 'none', margin: '0 0 4px' }}
            aria-label="Email"
          />
          <label style={fieldLabel}>
            Mobile number <span style={optTag}>(optional)</span>
          </label>
          {phoneBlock}
        </>
      )}

      {/* Two independent consent checkboxes — both unchecked and optional, each
          carrying the complete opt-in language. The reminders (non-marketing)
          checkbox sits first, directly beside its consent language. The leading
          "Optional —" is UI chrome, not part of the stored consent sentence. */}
      <label style={checkRow}>
        <input type="checkbox" checked={reminders} onChange={(e) => setReminders(e.target.checked)} />
        <span><span style={optionalPrefix}>Optional — </span>{disc.consent.transactional}</span>
      </label>
      <label style={checkRow}>
        <input type="checkbox" checked={promotions} onChange={(e) => setPromotions(e.target.checked)} />
        <span><span style={optionalPrefix}>Optional — </span>{disc.consent.marketing}</span>
      </label>

      {/* Embedded step-2: phone appears once a consent box is ticked. */}
      {!standalone && anyChecked && phoneBlock}

      {/* Privacy assurance + legal links at the point of capture. */}
      <p style={discStyle}>
        Mobile opt-in information is never shared or sold to third parties.{' '}
        <a href={disc.termsUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>Terms</a>
        {' · '}
        <a href={disc.privacyUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>Privacy</a>
      </p>

      {error && <div style={{ color: '#c0392b', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      {note && <div style={{ color: '#6b6b80', fontSize: 13, marginBottom: 8 }}>{note}</div>}

      {(standalone || anyChecked) && (
        <button type="button" onClick={submit} disabled={status === 'saving'} style={btnStyle}>
          {status === 'saving' ? 'Saving…' : standalone ? 'Save my preferences' : 'Save my number'}
        </button>
      )}
    </div>
  );
}

const collapsedBtnStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: 'rgba(255,255,255,0.08)', color: '#fff',
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: 999,
  padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  marginTop: 16,
};
const cardBase: CSSProperties = {
  border: '1px solid #e6e1d8', borderRadius: 12, padding: '16px 18px',
  background: '#fbf9f6', marginTop: 16,
};
const checkRow: CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14,
  color: '#33334d', margin: '6px 0', cursor: 'pointer', lineHeight: 1.4,
};
const fieldLabel: CSSProperties = {
  display: 'block', fontWeight: 600, fontSize: 13, color: '#44445e', margin: '10px 0 4px',
};
const reqTag: CSSProperties = { fontWeight: 500, color: '#8a8472' };
const optTag: CSSProperties = { fontWeight: 500, color: '#8a8472' };
const optionalPrefix: CSSProperties = { fontWeight: 700, color: '#0A0A2E' };
const inputStyle: CSSProperties = {
  flex: '1 1 160px', padding: '10px 12px', borderRadius: 8,
  border: '1px solid #d8d2c7', fontSize: 15, color: '#0A0A2E',
};
const discStyle: CSSProperties = {
  fontSize: 11.5, color: '#8a8472', lineHeight: 1.5, margin: '6px 0 10px',
};
const linkStyle: CSSProperties = { color: '#F05A28', textDecoration: 'underline' };
const btnStyle: CSSProperties = {
  background: '#F05A28', color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 18px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
};
